import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box, Button, Typography, useTheme } from '@mui/material';
import { FormsPaths } from 'app/configs/routesConfig/forms';
import { uncompletedLoanErrorMessageParams } from 'app/pages/loanApplication/LoanApplication.constants';
import { TaskAssignmentForm } from 'app/pages/loanApplication/tabs/tasks';
import { FormViewer } from 'app/pages/system/forms';
import FormListItem from 'app/pages/system/forms/FormsTreeView/FormListItem/FormListItem';
import { getStyles } from 'app/pages/system/forms/FormsTreeView/FormsTreeView.styles';
import ScrollWrapper from 'app/shared/components/common/ScrollWrapper';
import DocumentIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/DocumentIcon';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';

import { FormViewerNavigationType } from 'constants/forms';
import useContentWidth from 'hooks/useContentWidth';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { getScrollStyles } from 'styles/scrollbar.styles';

import { getLoanSpecificForms, setFormNavigationTypeAction } from 'store/forms/actions';
import { selectLoanSpecificForms } from 'store/forms/selectors';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import { showMessageThatCurrentUserCantChangeLoanAction } from 'store/loanApplications/actions';
import {
  selectIsCurrentUserCanChangeLoan,
  selectIsLoanInitiatedWithErrors,
} from 'store/loanApplications/selectors';
import { getTaskCategoriesAction, getTaskTypesAction } from 'store/system/actions';

import { DocumentsTestIds } from './Documents.testIds';
import {
  emptyStateBodyStyles,
  emptyStateContainerStyles,
  emptyStateIconWrapperStyles,
  emptyStateTitleStyles,
} from './LoanSpecificDocuments.styles';

const LoanSpecificDocuments = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const scrollClasses = getScrollStyles(theme);
  const classes = getStyles(theme, { isView: true });

  const loanId = useLoanId();

  const { applicationId } = useParams();

  const treeRef = useRef();
  const [selectedForm, setSelectedForm] = useState(null);

  const loanSpecificForms = useSelector(selectLoanSpecificForms);
  const isLoanInitedWithErrors = useSelector(selectIsLoanInitiatedWithErrors);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);

  const { containerRef, contentWidth, setContentWidth, setRef } = useContentWidth();

  useEffectSafeStrictMode(() => {
    if (!loanId) {
      return;
    }

    setIsLoading(true);

    Promise.allSettled([dispatch(getTaskCategoriesAction()), dispatch(getTaskTypesAction())])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, loanId, applicationId]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    setContentWidth(containerRef.current.clientWidth);
  }, [containerRef, setContentWidth]);

  useEffect(() => {
    dispatch(setFormNavigationTypeAction(FormViewerNavigationType.STATE));
  }, [dispatch]);

  const onAssignTask = useCallback(() => {
    if (isLoanInitedWithErrors) {
      return dispatch(showMessageAction(uncompletedLoanErrorMessageParams));
    }

    if (!isCurrentUserCanChangeLoan) {
      return dispatch(showMessageThatCurrentUserCantChangeLoanAction());
    }

    dispatch(
      openDialogAction({
        header: 'Assign Task',
        body: dialogProps => (
          <TaskAssignmentForm isHideStateField selectedForm={selectedForm} {...dialogProps} />
        ),
      }),
    );
  }, [isLoanInitedWithErrors, isCurrentUserCanChangeLoan, dispatch, selectedForm]);

  const onSelectedFormById = useCallback(
    form => {
      if (!form) {
        setSelectedForm(null);
        dispatch(getLoanSpecificForms({ loanId, applicationId })).catch(Error);

        return;
      }

      setSelectedForm({
        name: form.name,
        formId: form.formId,
        historyEntityId: undefined,
        formType: form.formType,
        selectedPage: 1,
      });
    },
    [applicationId, dispatch, loanId],
  );

  const onBrowseFormLibrary = useCallback(() => {
    history.push(FormsPaths.forms.path);
  }, [history]);

  if (!loanSpecificForms.length) {
    return (
      <Box sx={emptyStateContainerStyles}>
        <Box sx={emptyStateIconWrapperStyles}>
          <DocumentIcon size={28} />
        </Box>
        <Typography sx={emptyStateTitleStyles} variant="bold14">
          No loan-specific forms yet
        </Typography>
        <Typography sx={emptyStateBodyStyles} variant="regular14">
          We couldn&apos;t find any forms associated with this loan. Forms appear here once
          they&apos;re attached to the loan.
        </Typography>
        <Button
          data-testid={DocumentsTestIds.LOAN_SPECIFIC_DOCUMENTS_BROWSE_LIBRARY_BUTTON}
          variant="outlined"
          onClick={onBrowseFormLibrary}
        >
          Browse form library
        </Button>
      </Box>
    );
  }

  return (
    <div className="flex items-start flex-1 overflow-hidden h-screen" ref={setRef}>
      <Box sx={classes.treeContainer}>
        <ScrollWrapper isDragging={false} scrollRef={treeRef}>
          <div className={clsx(scrollClasses.scroll, 'relative overflow-y-auto h-full')}>
            {loanSpecificForms.map(form => (
              <FormListItem
                isHideTag
                form={form}
                key={form.formId}
                navigationType={FormViewerNavigationType.STATE}
                selectedFormId={selectedForm ? selectedForm.formId : undefined}
                onClickFormItem={onSelectedFormById}
              />
            ))}
          </div>
        </ScrollWrapper>
      </Box>
      <div className="w-full h-full overflow-hidden">
        {!!selectedForm && (
          <FormViewer
            isDisableResetStateAction
            contentWidth={contentWidth}
            formParams={selectedForm}
            onEmailTask={onAssignTask}
          />
        )}
      </div>
    </div>
  );
};

export default withLoading(LoanSpecificDocuments);

LoanSpecificDocuments.propTypes = {
  ...WithLoadingPropTypes,
};
